import Link from "next/link";

import { useTranslation } from "@flare/i18n";
import { Heading, Icon, Label, Text } from "@flare/ui";

import { Skeleton } from "@/components/Skeleton";
import { useFunnelAnswers } from "@/modules/v2/funnel/hooks/useFunnelAnswers";
import type { StepExperiments } from "@/modules/v2/funnel/steps/step.types";

import { StickyActions } from "../base/StickyActions";

function FieldSkeleton({ label }: { label: string }) {
  return (
    <div className="flex w-full flex-col gap-sp-2">
      <Label className="opacity-20">{label}</Label>
      <Skeleton className="h-s-6" />
    </div>
  );
}

export function FormSkeleton({
  experiments,
}: {
  experiments: StepExperiments;
}) {
  const { t } = useTranslation();
  const { abTestDivorceFlowEnabled } = experiments;
  const { funnelAnswers } = useFunnelAnswers();
  const { subPractice } = funnelAnswers;

  const isAbTestDivorceFlowEnabled =
    abTestDivorceFlowEnabled && subPractice === "divorce";

  return (
    <div className="flex flex-col gap-sp-7">
      <div className="flex flex-col gap-sp-1">
        <Heading variant="h3" className="text-center font-normal opacity-20">
          {isAbTestDivorceFlowEnabled
            ? t("FUNNEL_CHECKOUT_FORM_TITLE_V1")
            : t("FUNNEL_CHECKOUT_FORM_TITLE")}
        </Heading>
        {isAbTestDivorceFlowEnabled && (
          <Text variant="body1" className="text-center opacity-20">
            {t("FUNNEL_CHECKOUT_FORM_SUBTITLE")}
          </Text>
        )}
      </div>
      <Skeleton className="h-40" />
      <div className="flex flex-col gap-sp-5">
        <Heading variant="h4" className="font-medium opacity-20">
          {t("FUNNEL_CHECKOUT_FORM_PAYMENT_TITLE")}
        </Heading>
        <FieldSkeleton label={t("FUNNEL_CHECKOUT_FORM_NAME_LABEL")} />
        <FieldSkeleton label={t("FUNNEL_CHECKOUT_FORM_CARD_NUMBER_LABEL")} />
        <div className="flex flex-col gap-sp-5 md:flex-row md:justify-between md:gap-sp-3">
          <FieldSkeleton
            label={t("FUNNEL_CHECKOUT_FORM_EXPIRATION_DATE_LABEL")}
          />
          <FieldSkeleton label={t("FUNNEL_CHECKOUT_FORM_CVC_LABEL")} />
        </div>
        <FieldSkeleton label={t("FUNNEL_CHECKOUT_FORM_ZIP_CODE_LABEL")} />
      </div>
      <div className="flex items-center gap-sp-2">
        <Icon name="shield-check" className="opacity-20" />
        <Label className="font-medium opacity-20">
          {t("FUNNEL_CHECKOUT_FORM_SSL_ENCRYPTED")}
        </Label>
      </div>
      <Text variant="caption" className="text-center opacity-20">
        {t("FUNNEL_CHECKOUT_FORM_AUTHORIZATION_TEXT_1")}{" "}
        <Link target="_blank" href="/privacy" className="text-green-100">
          {t("PRIVACY_POLICY")} &{" "}
        </Link>
        <Link target="_blank" href="/terms-of-use" className="text-green-100">
          {t("TERMS_OF_USE")}.{" "}
        </Link>
        {t("FUNNEL_CHECKOUT_FORM_AUTHORIZATION_TEXT_2")}
      </Text>
      <StickyActions className="gap-4 flex flex-col items-center justify-center md:w-full md:self-center">
        <Skeleton className="h-14 w-full max-w-[350px]" />
      </StickyActions>
    </div>
  );
}
